/**
 * Entity Layer Managers - Exports Index
 *
 * Este archivo facilita la importación de todos los managers de entidades.
 *
 * Uso:
 * ```typescript
 * import {
 *   VehiculosLayerManager,
 *   LuminariasLayerManager,
 *   ALL_MANAGERS
 * } from './mapbox/entity-managers';
 * ```
 */

// ========== MANAGERS INDIVIDUALES ==========

// Managers simples (una capa, sin estados complejos)
export * from './activos-layer.manager';
export * from './alarmas-layer.manager';
export * from './alertas-seguridad-layer.manager';
export * from './eventos-seguridad-layer.manager';
export * from './zonas-seguridad-layer.manager';
export * from './localidades-seguridad-layer.manager';
export * from './paradas-layer.manager';
export * from './puntos-layer.manager';

// Managers con dos capas (background + foreground, orientación)
export * from './vehiculos-layer.manager';
export * from './colectivos-layer.manager';
export * from './trackers-layer.manager';

// Managers con estados dinámicos
export * from './sirenas-layer.manager';
export * from './gateways-layer.manager';

// Managers complejos (estados + zoom awareness)
export * from './luminarias-layer.manager';

// Managers de emergencias
export * from './emergencias-medicas-layer.manager';
export * from './hospitales-layer.manager';
export * from './ambulancias-layer.manager';
export * from './centros-atencion-layer.manager';

// Managers de geometrías (no usan EntityLayerManager base)
export * from './polylines-layer.manager';
export * from './polygons-layer.manager';
export * from './heatmap-layer.manager';

// ========== IMPORTS PARA ARRAY ==========

import { ActivosLayerManager } from './activos-layer.manager';
import { AlarmasLayerManager } from './alarmas-layer.manager';
import { AlertasSeguridadLayerManager } from './alertas-seguridad-layer.manager';
import { EventosSeguridadLayerManager } from './eventos-seguridad-layer.manager';
import { ParadasLayerManager } from './paradas-layer.manager';
import { PuntosLayerManager } from './puntos-layer.manager';
import { VehiculosLayerManager } from './vehiculos-layer.manager';
import { ColectivosLayerManager } from './colectivos-layer.manager';
import { TrackersLayerManager } from './trackers-layer.manager';
import { SirenasLayerManager } from './sirenas-layer.manager';
import { GatewaysLayerManager } from './gateways-layer.manager';
import { LuminariasLayerManager } from './luminarias-layer.manager';
import { EmergenciasMedicasLayerManager } from './emergencias-medicas-layer.manager';
import { HospitalesLayerManager } from './hospitales-layer.manager';
import { AmbulanciasLayerManager } from './ambulancias-layer.manager';
import { CentrosAtencionLayerManager } from './centros-atencion-layer.manager';
import { IEntityLayerManager } from '../core/interfaces/entity-layer-manager.interface';

// Geometry managers (no implementan IEntityLayerManager)
import { PolylinesLayerManager } from './polylines-layer.manager';
import { PolygonsLayerManager } from './polygons-layer.manager';
import { HeatmapLayerManager } from './heatmap-layer.manager';

// ========== ARRAY DE TODOS LOS MANAGERS ==========

/**
 * Array con las clases de todos los managers disponibles.
 *
 * Útil para registro automático en el EntityManagerRegistry.
 *
 * Ejemplo de uso:
 * ```typescript
 * constructor(
 *   private entityRegistry: EntityManagerRegistry,
 *   private injector: Injector
 * ) {
 *   // Registrar todos los managers automáticamente
 *   ALL_MANAGER_CLASSES.forEach(ManagerClass => {
 *     const manager = this.injector.get(ManagerClass);
 *     this.entityRegistry.register(manager);
 *   });
 * }
 * ```
 */

//MANAGERS DE LAS ENTIDADES
export const ALL_ENTITY_MANAGER_CLASSES = [
  ActivosLayerManager,
  AlarmasLayerManager,
  AlertasSeguridadLayerManager,
  EventosSeguridadLayerManager,
  ParadasLayerManager,
  PuntosLayerManager,
  VehiculosLayerManager,
  ColectivosLayerManager,
  TrackersLayerManager,
  SirenasLayerManager,
  GatewaysLayerManager,
  LuminariasLayerManager,
  EmergenciasMedicasLayerManager,
  HospitalesLayerManager,
  AmbulanciasLayerManager,
  CentrosAtencionLayerManager,
] as const;

//MANAGERS DE GEOMETRÍAS
export const ALL_GEOMETRY_MANAGER_CLASSES = [
  PolylinesLayerManager,
  PolygonsLayerManager,
  HeatmapLayerManager,
] as const;

/**
 * Tipo helper para extraer los tipos de todos los managers
 */
export type AllEntityManagerTypes =
  | ActivosLayerManager
  | AlarmasLayerManager
  | AlertasSeguridadLayerManager
  | EventosSeguridadLayerManager
  | ParadasLayerManager
  | PuntosLayerManager
  | VehiculosLayerManager
  | ColectivosLayerManager
  | TrackersLayerManager
  | SirenasLayerManager
  | GatewaysLayerManager
  | LuminariasLayerManager
  | EmergenciasMedicasLayerManager
  | HospitalesLayerManager
  | AmbulanciasLayerManager
  | CentrosAtencionLayerManager;

export type AllGeometryManagerTypes =
  | PolylinesLayerManager
  | PolygonsLayerManager
  | HeatmapLayerManager;

/**
 * Mapa de entityType a clase de manager
 *
 * Útil para obtener el tipo de manager por su entityType.
 */
export const ENTITY_MANAGER_BY_TYPE = {
  activos: ActivosLayerManager,
  alarmas: AlarmasLayerManager,
  alertasSeguridad: AlertasSeguridadLayerManager,
  eventosSeguridad: EventosSeguridadLayerManager,
  paradas: ParadasLayerManager,
  puntos: PuntosLayerManager,
  vehiculos: VehiculosLayerManager,
  colectivos: ColectivosLayerManager,
  trackers: TrackersLayerManager,
  sirenas: SirenasLayerManager,
  gateways: GatewaysLayerManager,
  luminarias: LuminariasLayerManager,
  emergenciasMedicas: EmergenciasMedicasLayerManager,
  hospitales: HospitalesLayerManager,
  ambulancias: AmbulanciasLayerManager,
  centrosAtencion: CentrosAtencionLayerManager,
} as const;

/**
 * Tipo para los entityTypes disponibles
 */
export type EntityType = keyof typeof ENTITY_MANAGER_BY_TYPE;

/**
 * Información de cada manager (metadata)
 */
export interface ManagerInfo {
  entityType: string;
  className: string;
  supportsClustering: boolean;
  clusterColor?: string;
  complexity: 'simple' | 'medium' | 'complex';
  features: string[];
}

/**
 * Metadata de todos los managers disponibles
 *
 * Útil para debugging, documentación automática, etc.
 */
export const MANAGERS_INFO: ManagerInfo[] = [
  {
    entityType: 'activos',
    className: 'ActivosLayerManager',
    supportsClustering: true,
    clusterColor: '#4CAF50',
    complexity: 'simple',
    features: ['clustering', 'pin único'],
  },
  {
    entityType: 'alarmas',
    className: 'AlarmasLayerManager',
    supportsClustering: true,
    clusterColor: '#F44336',
    complexity: 'simple',
    features: ['clustering', 'icono casa-escudo'],
  },
  {
    entityType: 'alertasSeguridad',
    className: 'AlertasSeguridadLayerManager',
    supportsClustering: true,
    clusterColor: '#E53935',
    complexity: 'simple',
    features: ['clustering', 'sólo alertas activas', 'icono triángulo rojo'],
  },
  {
    entityType: 'paradas',
    className: 'ParadasLayerManager',
    supportsClustering: false,
    complexity: 'simple',
    features: ['text labels', 'sin clustering'],
  },
  {
    entityType: 'vehiculos',
    className: 'VehiculosLayerManager',
    supportsClustering: true,
    clusterColor: '#2195f3',
    complexity: 'medium',
    features: ['clustering', 'dos capas', 'orientación rotable'],
  },
  {
    entityType: 'colectivos',
    className: 'ColectivosLayerManager',
    supportsClustering: true,
    clusterColor: '#ff7300',
    complexity: 'medium',
    features: ['clustering', 'dos capas', 'orientación rotable'],
  },
  {
    entityType: 'trackers',
    className: 'TrackersLayerManager',
    supportsClustering: true,
    clusterColor: '#9C27B0',
    complexity: 'medium',
    features: ['clustering', 'dos capas', 'orientación rotable'],
  },
  {
    entityType: 'sirenas',
    className: 'SirenasLayerManager',
    supportsClustering: true,
    clusterColor: '#000000',
    complexity: 'medium',
    features: ['clustering', 'estados dinámicos (online/offline)'],
  },
  {
    entityType: 'gateways',
    className: 'GatewaysLayerManager',
    supportsClustering: false,
    complexity: 'medium',
    features: ['estados de salud (ok/warning/error)', 'iconos SVG'],
  },
  {
    entityType: 'luminarias',
    className: 'LuminariasLayerManager',
    supportsClustering: true,
    clusterColor: '#fad66a',
    complexity: 'complex',
    features: [
      'clustering',
      '6 estados dinámicos',
      'zoom awareness (icons vs circles)',
      'modo específico vs general',
    ],
  },
  {
    entityType: 'emergenciasMedicas',
    className: 'EmergenciasMedicasLayerManager',
    supportsClustering: false,
    complexity: 'medium',
    features: [
      '7 estados dinámicos',
      'iconos por estado',
      'sin clustering (crítico)',
    ],
  },
  {
    entityType: 'hospitales',
    className: 'HospitalesLayerManager',
    supportsClustering: false,
    complexity: 'simple',
    features: ['icono simple', 'sin clustering'],
  },
  {
    entityType: 'ambulancias',
    className: 'AmbulanciasLayerManager',
    supportsClustering: false,
    complexity: 'medium',
    features: ['dos capas', 'orientación rotable', 'asignada vs disponible'],
  },
  {
    entityType: 'centrosAtencion',
    className: 'CentrosAtencionLayerManager',
    supportsClustering: false,
    complexity: 'medium',
    features: [
      'círculos de cobertura',
      'marker de centro',
      'áreas de influencia',
    ],
  },
];

/**
 * Metadata de geometry managers
 */
export const GEOMETRY_MANAGERS_INFO = [
  {
    className: 'PolylinesLayerManager',
    type: 'polylines',
    features: ['recorridos', 'rutas', 'líneas con estilo', 'líneas punteadas'],
  },
  {
    className: 'PolygonsLayerManager',
    type: 'polygons',
    features: [
      'zonas',
      'áreas',
      'polígonos con relleno',
      'bordes configurables',
    ],
  },
  {
    className: 'HeatmapLayerManager',
    type: 'heatmap',
    features: [
      'mapas de calor',
      'densidad de puntos',
      'gradiente configurable',
    ],
  },
];
